import React, { useState, useRef, useEffect } from "react";
import { API } from "@/lib/api";
import { ChatCircleDots, X, PaperPlaneRight, Sparkle } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "How do you verify suppliers?",
  "What are your shipping options?",
  "Help me start an Amazon brand",
];

const newMsg = (role, content) => ({ id: crypto.randomUUID(), role, content });

async function streamChat({ sessionId, message, onSession, onDelta }) {
  const res = await fetch(`${API}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, message }),
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n\n");
    buffer = lines.pop();
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const payload = line.slice(6);
      if (payload === "[DONE]") continue;
      try {
        const obj = JSON.parse(payload);
        if (obj.session_id) onSession(obj.session_id);
        if (obj.delta) onDelta(obj.delta);
      } catch (e) {
        console.debug("Skipping malformed SSE chunk:", e);
      }
    }
  }
}

function MessageBubble({ m, pending }) {
  return (
    <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`chat-message-${m.role}`}
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
          m.role === "user"
            ? "rounded-br-sm bg-ink text-white"
            : "rounded-bl-sm border border-slate-200 bg-white text-slate-700"
        }`}
      >
        {m.content || (pending ? "…" : "")}
      </div>
    </div>
  );
}

function SuggestionList({ onPick }) {
  return (
    <div className="space-y-2 pt-2">
      {SUGGESTIONS.map((s) => (
        <button
          key={s}
          onClick={() => onPick(s)}
          data-testid="chat-suggestion"
          className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-600 transition-colors hover:border-teal hover:text-ink"
        >
          {s}
        </button>
      ))}
    </div>
  );
}

function ChatInput({ value, onChange, onSend, disabled }) {
  return (
    <div className="flex items-center gap-2 border-t border-slate-200 bg-white p-3">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSend()}
        placeholder="Ask about sourcing, shipping…"
        data-testid="chat-input"
        className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30"
      />
      <button
        onClick={() => onSend()}
        disabled={disabled}
        data-testid="chat-send-button"
        className="flex h-9 w-9 items-center justify-center rounded-lg bg-ink text-white transition-colors hover:bg-ink-800 disabled:opacity-50"
      >
        <PaperPlaneRight size={18} weight="fill" />
      </button>
    </div>
  );
}

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", content: "Hi! I'm Uni, your trade assistant. Ask me about sourcing, shipping, quality control or launching your brand from China." },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const replaceLast = (updater) =>
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = updater(copy[copy.length - 1]);
      return copy;
    });

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, newMsg("user", message), newMsg("assistant", "")]);
    setStreaming(true);
    try {
      await streamChat({
        sessionId,
        message,
        onSession: setSessionId,
        onDelta: (delta) => replaceLast((last) => ({ ...last, content: last.content + delta })),
      });
    } catch (e) {
      console.error("Chat stream failed:", e);
      replaceLast((last) => ({ ...last, content: "Sorry, I'm unavailable right now. Please use the Request a Quote form." }));
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-chat-toggle"
        aria-label="Open AI assistant"
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white shadow-lg shadow-black/25 transition-transform duration-300 hover:-translate-y-1"
      >
        {open ? <X size={26} weight="bold" /> : <ChatCircleDots size={28} weight="fill" />}
      </button>

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-24 right-6 z-40 flex h-[520px] w-[92vw] max-w-[380px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
        >
          <div className="flex items-center gap-3 bg-ink px-4 py-3 text-white">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#1E73BE] to-[#16B894]">
              <Sparkle size={20} weight="fill" />
            </div>
            <div>
              <p className="font-heading text-sm font-semibold">Uni — Trade Assistant</p>
              <p className="text-[11px] text-slate-300">Powered by AI · replies in seconds</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-mist/60 p-4">
            {messages.map((m, i) => (
              <MessageBubble key={m.id} m={m} pending={streaming && i === messages.length - 1} />
            ))}
            {messages.length <= 1 && <SuggestionList onPick={send} />}
          </div>

          <ChatInput value={input} onChange={setInput} onSend={send} disabled={streaming} />
        </div>
      )}
    </>
  );
}
